import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { getSetterIdsCache, setSetterIdsCache } from "../lib/setterPerformanceCacheService";

// =============================================================================
// USE ORG SETTER IDS HOOK
// =============================================================================
// PURPOSE: Dynamically fetch active setter GHL user IDs for the current org.
//
// SOURCE OF TRUTH: organization_members (role + status) → profiles (ghl_user_id)
// NOT ghl_users — that's a GHL API mirror, not the Altara role authority.
//
// USAGE:
//   const { setterIds, isLoading } = useOrgSetterIds(currentOrganization?.id || null);
//   // Pass setterIds to useSetterPerformance, SetterProofModal, etc.
// =============================================================================

export interface UseOrgSetterIdsResult {
  setterIds: string[];
  isLoading: boolean;
}

export function useOrgSetterIds(organizationId: string | null): UseOrgSetterIdsResult {
  // Initialize from cache synchronously — eliminates the empty-array render cycle
  const [setterIds, setSetterIds] = useState<string[]>(
    () => (organizationId ? getSetterIdsCache(organizationId) : null) ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!organizationId || !supabase) {
      setSetterIds([]);
      return;
    }

    // Abort previous request on org switch
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const fetchSetterIds = async () => {
      // Stale-while-revalidate: show cached data instantly
      const cached = getSetterIdsCache(organizationId);
      if (cached) {
        setSetterIds(cached);
      } else {
        setIsLoading(true);
      }

      try {
        const { data, error } = await supabase!
          .from("organization_members")
          .select("user_id, profiles!inner(ghl_user_id)")
          .eq("organization_id", organizationId)
          .eq("role", "setter")
          .eq("status", "active");

        if (error) throw error;

        // Extract ghl_user_ids, filter out nulls
        const ids = (data || [])
          .map((row) => (row.profiles as unknown as { ghl_user_id: string | null })?.ghl_user_id)
          .filter((id): id is string => id != null && id.length > 0);

        setSetterIdsCache(organizationId, ids);
        setSetterIds(ids);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (!cached) {
          console.warn("Failed to fetch setter IDs:", err);
          setSetterIds([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetterIds();

    return () => {
      abortRef.current?.abort();
    };
  }, [organizationId]);

  return { setterIds, isLoading };
}
